import { useState } from "react";
import { usePaymentMutations, usePaymentSummary } from "@/features/payments/hooks/paymentQueries";
import { formatPaymentMoney, paymentStatusLabels, type CashPayment, type Payment } from "@/features/payments/types/paymentTypes";
import { Button } from "@/shared/components/shadcn/Button";
import { Card } from "@/shared/components/ui/Card";
import { ConfirmationDialog } from "@/shared/components/ui/ConfirmationDialog";
import { EmptyState } from "@/shared/components/ui/EmptyState";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { RowSkeleton } from "@/shared/components/ui/RowSkeleton";
import { Section } from "@/shared/components/ui/Section";
import { formatBusinessDate } from "@/shared/utils/formatters";
import { useAppointmentEvents } from "../hooks/appointmentQueries";
import type { AppointmentEvent } from "../types/appointmentEventTypes";

const actors: Record<AppointmentEvent["actorType"], string> = {
  owner: "Business owner",
  customer: "Customer",
  system: "System",
  unknown: "",
};

const statusTitles: Record<AppointmentEvent["toStatus"], string> = {
  pending: "Appointment pending",
  confirmed: "Appointment confirmed",
  completed: "Appointment completed",
  cancelled: "Appointment cancelled",
  declined: "Appointment declined",
  no_show: "Appointment marked as no show",
};

function eventTitle(event: AppointmentEvent) {
  if (event.eventType === "created") return "Appointment created";
  if (event.eventType === "rescheduled") return "Appointment rescheduled";
  return statusTitles[event.toStatus];
}

function historyDateFormatter(timeZone: string, currentYear: string) {
  return (date: Date) => formatBusinessDate(date, timeZone, {
    year: formatBusinessDate(date, timeZone, { year: "numeric" }) === currentYear ? undefined : "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function PaymentHistoryEntry({ payment, timeZone, currentYear }: {
  payment: Payment;
  timeZone: string;
  currentYear: string;
}) {
  const formatDate = historyDateFormatter(timeZone, currentYear);
  return (
    <li className="space-y-2 py-4 first:pt-0 last:pb-0">
      <p className="text-sm font-semibold">Payment {formatPaymentMoney(payment.amountMinor, payment.currency)}</p>
      <p className="text-xs text-muted-foreground">
        <time dateTime={payment.createdAt.toISOString()}>{formatDate(payment.createdAt)}</time>
        {" "}&middot; {paymentStatusLabels[payment.status]}
      </p>
      {payment.card && <p className="text-sm">{payment.card.brand} ending in {payment.card.last4}</p>}
      {payment.refundedAmountMinor > 0 && (
        <p className="text-sm">Refunded {formatPaymentMoney(payment.refundedAmountMinor, payment.currency)}</p>
      )}
      {payment.disputeStatus && <p className="text-sm">Dispute: {payment.disputeStatus}. Review this payment in Stripe.</p>}
      {payment.failureMessage && <p className="whitespace-pre-wrap break-words text-sm">{payment.failureMessage}</p>}
    </li>
  );
}

function CashHistoryEntry({ payment, timeZone, currentYear, onVoid }: {
  payment: CashPayment;
  timeZone: string;
  currentYear: string;
  onVoid: (payment: CashPayment) => void;
}) {
  const formatDate = historyDateFormatter(timeZone, currentYear);
  return (
    <li className="flex items-start justify-between gap-3 py-4 first:pt-0 last:pb-0">
      <div className="min-w-0 space-y-2">
        <p className="text-sm font-semibold">Cash {formatPaymentMoney(payment.amountMinor, payment.currency)}</p>
        <p className="text-xs text-muted-foreground">
          <time dateTime={payment.createdAt.toISOString()}>{formatDate(payment.createdAt)}</time>
          {" "}&middot; {payment.voidedAt ? "Voided" : "Paid"}
        </p>
        {payment.voidedAt && <p className="text-sm">Voided {formatDate(payment.voidedAt)}</p>}
      </div>
      {!payment.voidedAt && <Button variant="quiet" onClick={() => onVoid(payment)}>Void</Button>}
    </li>
  );
}

function HistoryEntry({ event, timeZone, currentYear }: {
  event: AppointmentEvent;
  timeZone: string;
  currentYear: string;
}) {
  const formatDate = historyDateFormatter(timeZone, currentYear);
  const actor = actors[event.actorType];

  return (
    <li className="space-y-2 py-4 first:pt-0 last:pb-0">
      <p className="text-sm font-semibold">{eventTitle(event)}</p>
      <p className="text-xs text-muted-foreground">
        <time dateTime={event.createdAt.toISOString()}>{formatDate(event.createdAt)}</time>
        {actor && <> &middot; {actor}</>}
      </p>
      {event.eventType === "rescheduled" && (
        <div className="space-y-1 text-sm">
          <p>Previous: {event.oldSchedule?.startAt ? formatDate(event.oldSchedule.startAt) : "Start not recorded"}</p>
          <p>New: {event.newSchedule.startAt ? formatDate(event.newSchedule.startAt) : "Start not recorded"}</p>
          {event.oldSchedule && event.oldSchedule.locationId !== event.newSchedule.locationId && (
            <p>Appointment location changed.</p>
          )}
        </div>
      )}
      {event.reason && <p className="whitespace-pre-wrap break-words text-sm">Reason: {event.reason}</p>}
    </li>
  );
}

export function AppointmentHistory({ appointmentId, timeZone }: { appointmentId: string; timeZone: string }) {
  const query = useAppointmentEvents(appointmentId);
  const paymentsQuery = usePaymentSummary(appointmentId);
  const { voidCash } = usePaymentMutations(appointmentId);
  const [voiding, setVoiding] = useState<CashPayment | null>(null);
  const currentYear = formatBusinessDate(new Date(), timeZone, { year: "numeric" });
  const error = query.error || paymentsQuery.error;
  const refetch = () => {
    void query.refetch();
    void paymentsQuery.refetch();
  };
  const entries = [
    ...(query.data ?? []).map((event) => ({ kind: "event" as const, createdAt: event.createdAt, event })),
    ...(paymentsQuery.data?.payments ?? []).map((payment) => ({ kind: "payment" as const, createdAt: payment.createdAt, payment })),
    ...(paymentsQuery.data?.cashPayments ?? []).map((cash) => ({ kind: "cash" as const, createdAt: cash.createdAt, cash })),
  ].sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());

  return (
    <Section
      title="Appointment history"
      action={
        <Button variant="quiet" disabled={query.isFetching || paymentsQuery.isFetching} onClick={refetch}>
          Refresh history
        </Button>
      }
    >
      {query.isLoading || paymentsQuery.isLoading ? (
        <RowSkeleton />
      ) : error ? (
        <ErrorState title="Couldn't load appointment history" message={error.message} onRetry={refetch} />
      ) : !entries.length ? (
        <EmptyState message="No appointment history recorded." />
      ) : (
        <Card>
          <ol className="divide-y divide-border" aria-label="Appointment history events">
            {entries.map((entry) => entry.kind === "event" ? (
              <HistoryEntry key={entry.event.id} event={entry.event} timeZone={timeZone} currentYear={currentYear} />
            ) : entry.kind === "payment" ? (
              <PaymentHistoryEntry key={entry.payment.id} payment={entry.payment} timeZone={timeZone} currentYear={currentYear} />
            ) : (
              <CashHistoryEntry key={entry.cash.id} payment={entry.cash} timeZone={timeZone} currentYear={currentYear}
                onVoid={(cash) => { voidCash.reset(); setVoiding(cash); }} />
            ))}
          </ol>
        </Card>
      )}
      <ConfirmationDialog open={!!voiding} onOpenChange={(open) => { if (!open && !voidCash.isPending) setVoiding(null); }}
        title="Void cash payment?"
        description={voiding ? `The ${formatPaymentMoney(voiding.amountMinor, voiding.currency)} cash payment will no longer count as paid. It stays in the history as voided.` : undefined}
        confirmLabel="Void payment" dismissLabel="Keep payment" destructive pending={voidCash.isPending} error={voidCash.error?.message}
        onConfirm={() => { if (voiding) voidCash.mutate(voiding.id, { onSuccess: () => setVoiding(null) }); }} />
    </Section>
  );
}
